/** How supply specs read on screen, and how facet filters live in the page URL. */
import { formatInteger, formatMoney, formatNumber, MISSING_VALUE } from "@/lib/format";
import { countryName } from "@/lib/regions";
import type { FacetCondition, FacetCount, FacetDef, PriceCheck, SpecValue, UnitBasis } from "./api";

/** Units as perfumers write them. */
const UNIT_LABELS: Record<string, string> = { ul: "µl", µl: "µl", pct: "%", pcs: "pcs" };

export function unitLabel(unit: string): string {
  return UNIT_LABELS[unit] ?? unit;
}

const UNIT_BASES: Record<UnitBasis, { noun: string; symbol: string; extraDecimals: number; missing: string }> = {
  piece: { noun: "piece", symbol: "piece", extraDecimals: 2, missing: "The store doesn’t say how many come in a pack" },
  litre: { noun: "litre", symbol: "L", extraDecimals: 0, missing: "The store doesn’t say how much the pack holds" },
  kg: { noun: "kg", symbol: "kg", extraDecimals: 0, missing: "The store doesn’t say how much the pack holds" },
};

/** "Per piece", "Per litre", "Per kg". */
export function perUnitLabel(basis: UnitBasis): string {
  return `Per ${UNIT_BASES[basis].noun}`;
}

/** The unit after a unit price: "CA$13.50 / L". */
export function unitSymbol(basis: UnitBasis): string {
  return UNIT_BASES[basis].symbol;
}

/** Decimals beyond the currency's: a vial costs fractions of a cent, a litre of alcohol doesn't. */
export function unitPriceDecimals(basis: UnitBasis): number {
  return UNIT_BASES[basis].extraDecimals;
}

/** Why a supply's unit price looks off: "CA$0.0200 / piece is 1/55 of the CA$1.1000 typical for bottles:
 * glass, 8–16 ml (11 compared)." */
export function priceCheckText(check: PriceCheck): string {
  const money = (minor: string) =>
    formatMoney(minor, check.currency, { extraDecimals: unitPriceDecimals(check.basis) });
  const ratio = Number(check.ratio);
  const times =
    ratio < 1 ? `1/${formatInteger(Math.round(1 / ratio))} of` : `${formatInteger(Math.round(ratio))} times`;
  return (
    `${money(check.unit_minor)} / ${unitSymbol(check.basis)} is ${times} the ${money(check.typical_minor)} typical for ` +
    `${check.group} (${formatInteger(check.compared_with)} compared). The pack size may be wrong.`
  );
}

/** Why a priced supply has no unit price. */
export function missingUnitPriceReason(basis: UnitBasis): string {
  return UNIT_BASES[basis].missing;
}

/** A facet as a column header, with the column's unit: "Capacity (ml)". */
export function columnHeader(facet: Pick<FacetDef, "label" | "kind" | "unit">): string {
  const unit = unitLabel(facet.unit);
  return facet.kind === "number" && unit && unit !== "pcs" ? `${facet.label} (${unit})` : facet.label;
}

/** A spec sheet or page as link text: "Spec sheet: 02991944.pdf". */
export function documentLabel(document: { url: string; kind: string }): string {
  const kind =
    document.kind === "spec_sheet"
      ? "Spec sheet"
      : document.kind
        ? `${document.kind.charAt(0).toUpperCase()}${document.kind.slice(1).replace(/_/g, " ")}`
        : "Document";
  const name = fileName(document.url);
  return name ? `${kind}: ${name}` : kind;
}

function fileName(url: string): string {
  try {
    const last = new URL(url).pathname.split("/").filter(Boolean).pop() ?? "";
    return decodeURIComponent(last);
  } catch {
    return "";
  }
}

/** "18-400" stays as written; "crimp 15 mm" reads "Crimp 15 mm (FEA 15)". */
export function neckFinishLabel(value: string): string {
  const crimp = /^crimp (\d+) mm$/.exec(value);
  if (crimp) return `Crimp ${crimp[1]} mm (FEA ${crimp[1]})`;
  const snap = /^snap (\d+) mm$/.exec(value);
  if (snap) return `Snap ${snap[1]} mm`;
  return value;
}

export function madeInLabel(value: string): string {
  if (value === "us_assembled") return "Assembled in the US";
  return /^[A-Z]{2}$/.test(value) ? countryName(value) : value;
}

/** A spec value with its unit or label: "4 ml", "PTFE-faced silicone", "Yes". `withUnit: false` leaves
 * the unit to a column header. */
export function formatSpec(
  facet: Pick<FacetDef, "key" | "kind" | "unit" | "values">,
  value: SpecValue | null | undefined,
  { withUnit = true }: { withUnit?: boolean } = {},
): string {
  if (value === null || value === undefined || value === "") return MISSING_VALUE;
  if (facet.kind === "boolean") return value === true || value === "true" ? "Yes" : "No";
  if (facet.kind === "number") {
    const unit = unitLabel(facet.unit);
    const number = formatNumber(String(value), { maxDecimals: 3 });
    return unit === "pcs" || !withUnit ? number : `${number} ${unit}`.trim();
  }
  const text = String(value);
  if (facet.key === "neck_finish") return neckFinishLabel(text);
  if (facet.key === "made_in") return madeInLabel(text);
  return facet.values.find((option) => option.value === text)?.label ?? text;
}

/** The label of an enum value in a facet count list (open enums get our readable forms). */
export function facetValueLabel(facet: FacetCount, value: string, label: string): string {
  if (facet.key === "neck_finish") return neckFinishLabel(value);
  if (facet.key === "made_in") return madeInLabel(value);
  return label;
}

/** A facet filter as the URL writes it: "5..15" for a range, "ptfe,ptfe_silicone" for values. */
export function conditionToParam(condition: FacetCondition): string {
  if ("values" in condition) return condition.values.join(",");
  return `${condition.min ?? ""}..${condition.max ?? ""}`;
}

export function rangeFromParam(param: string | null): { min: string | null; max: string | null } {
  if (!param) return { min: null, max: null };
  const [min, max] = param.split("..");
  return { min: min || null, max: max || null };
}

export function valuesFromParam(param: string | null): string[] {
  return param ? param.split(",").filter(Boolean) : [];
}

/** Where a value was read, as a short phrase. */
export function sourceLabel(source: string): string {
  return (
    {
      variant_specs: "SKU spec table",
      variant_options: "Variant options",
      variant_title: "Variant name",
      product_specs: "Product spec table",
      product_title: "Product title",
      description: "Description",
      document: "Spec sheet",
      variant_pack: "Pack size",
      implied: "Implied by other specs",
      manual: "Set by hand",
    }[source] ?? source
  );
}

/** The facets shown as columns of the results table, per category. */
export const CATEGORY_COLUMNS: Record<string, string[]> = {
  vials: ["capacity_ml", "neck_finish", "liner", "color", "cap_color"],
  bottles: ["capacity_ml", "neck_finish", "color", "shape"],
  "vials-bottles": ["capacity_ml", "neck_finish", "color", "liner", "shape"],
  "bottles-sprayers": ["capacity_ml", "neck_finish", "color", "shape"],
  "sample-sprayers": ["capacity_ml", "material", "attachment"],
  "spray-bottles": ["capacity_ml", "neck_finish", "color"],
  "spray-pumps": ["neck_finish", "pump_output_ul", "dip_tube_length_mm", "collar_finish"],
  closures: ["closure_type", "neck_finish", "liner"],
  pipettes: ["pipette_type", "capacity_ml", "length_mm", "graduated"],
  blotters: ["length_mm", "width_mm", "paper_gsm"],
  "lab-glassware": ["glassware_type", "capacity_ml", "glass_type"],
  filtration: ["filter_type", "membrane", "diameter_mm", "pore_um"],
  ethanol: ["abv_pct", "denaturant", "pack_volume_ml", "pack_count"],
  "solvents-additives": ["substance", "pack_volume_ml", "pack_mass_g"],
  tools: ["tool_type", "neck_finish"],
  "labels-packaging": ["material"],
  instruments: ["instrument_type", "readability_g", "max_capacity_g"],
};

/** Facets of a container whose neck finish says which caps and pumps fit it. */
export const FITTING_CATEGORIES: Record<string, string[]> = {
  vials: ["closures"],
  bottles: ["closures", "spray-pumps"],
  "spray-bottles": ["spray-pumps", "closures"],
  "sample-sprayers": ["spray-pumps"],
  closures: ["vials", "bottles"],
  "spray-pumps": ["bottles", "spray-bottles"],
};
